import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSessionQuery } from '../../features/auth/hooks';
import { clientManagementApi } from '../../features/admin/settings/api/clientManagement';
import type { ClientSummary } from '../../features/admin/settings/api/clientManagement';
import { qk } from '../../lib/queryKeys';

const STORAGE_KEY = 'peak4_selected_client_id';

interface ClientScopeContextValue {
  isCentralAdmin: boolean;
  clients: ClientSummary[];
  selectedClientId: string;
  setSelectedClientId: (clientId: string) => void;
  effectiveClientId: string | null;
  /** Whether the currently active client has anonymisation enabled. False for central admins; otherwise defaults to true until settings load. */
  isAnonymised: boolean;
  /**
   * Whether the currently selected client has anonymisation enabled.
   * True for every admin when the selected client has it on.
   * Use this when you want to hide individual-user data regardless of admin type.
   */
  selectedClientAnonymised: boolean;
}

const ClientScopeContext = createContext<ClientScopeContextValue | null>(null);

export const ClientScopeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: session } = useSessionQuery();
  const isCentralAdmin = session?.user?.is_central_admin ?? false;
  const userClientId = session?.user?.client_id ?? null;
  const [selectedClientId, setSelectedClientId] = useState(() => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem(STORAGE_KEY) || '';
  });

  const clientsQuery = useQuery({
    queryKey: qk.clients(),
    queryFn: () => clientManagementApi.listClients(),
    enabled: isCentralAdmin,
    staleTime: 5 * 60 * 1000,
  });

  // Regular admins: fetch their own client's details to get enable_anonymisation.
  const regularClientQuery = useQuery({
    queryKey: qk.clientDetails(userClientId),
    queryFn: () => clientManagementApi.getClientDetails(userClientId!),
    enabled: !isCentralAdmin && !!userClientId,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (selectedClientId) {
      localStorage.setItem(STORAGE_KEY, selectedClientId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [selectedClientId]);

  const value = useMemo<ClientScopeContextValue>(() => {
    const effectiveClientId = isCentralAdmin ? selectedClientId || null : userClientId;

    const allClients = clientsQuery.data?.data ?? [];
    // Fail closed while client settings are unavailable, including request errors.
    const selectedClientAnonymised = isCentralAdmin
      ? clientsQuery.data == null ||
        (selectedClientId
          ? (allClients.find((c) => c.id === selectedClientId)?.enableAnonymisation ?? true)
          : allClients.some((c) => c.enableAnonymisation !== false))
      : (regularClientQuery.data?.data?.enable_anonymisation ??
        regularClientQuery.data?.data?.enableAnonymisation ??
        true);
    const isAnonymised = !isCentralAdmin && selectedClientAnonymised;

    return {
      isCentralAdmin,
      clients: allClients,
      selectedClientId,
      setSelectedClientId,
      effectiveClientId,
      isAnonymised,
      selectedClientAnonymised,
    };
  }, [clientsQuery.data?.data, isCentralAdmin, regularClientQuery.data?.data, selectedClientId, userClientId]);

  return <ClientScopeContext.Provider value={value}>{children}</ClientScopeContext.Provider>;
};

export const useClientScope = () => {
  const context = useContext(ClientScopeContext);
  if (!context) {
    throw new Error('useClientScope must be used within ClientScopeProvider');
  }
  return context;
};
